import { useEffect, useRef } from 'react';

/** A field the operator has to fill outranks the ✕, which precedes it in the DOM. */
const FILLABLE = 'input[type="text"]:not([disabled]), input:not([type]):not([disabled]), textarea:not([disabled])';

/**
 * Opens a native `<dialog>` modally, so the browser owns what a hand-rolled overlay has to reinvent:
 * the rest of the page goes inert, Tab and Shift+Tab stay inside, and Escape asks to close. The scan
 * desk is a keyboard-only surface — an operator with a scanner in one hand never reaches for the ✕.
 *
 * Cancelling is refused rather than obeyed: the browser would close the dialog behind React's back and
 * leave the state that renders it saying otherwise, so `onClose` runs and the parent unmounts instead.
 * Focus goes back to whatever opened the dialog, so the desk keeps its place in the page behind.
 */
export const useModalDialog = (onClose: () => void) => {
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  // Runs once, on open. Callers pass an inline arrow for onClose, so depending on it re-ran the effect on
  // every keystroke and pulled focus back to the first control — only one character ever landed in a field.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }

    const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    dialog.querySelector<HTMLElement>(FILLABLE)?.focus();

    const cancel = (event: Event) => {
      event.preventDefault();
      close.current();
    };
    dialog.addEventListener('cancel', cancel);

    return () => {
      dialog.removeEventListener('cancel', cancel);
      invoker?.focus();
    };
  }, []);

  return ref;
};
